import * as React from 'react';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open, drawerwidth }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerwidth}px)`,
        marginLeft: `${drawerwidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Appbar = ({ drawerWidth, open, setOpen }) => {
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Page title mapping
    const getPageTitle = () => {
        const path = location.pathname;
        const titleMap = {
            '/': 'Dashboard',
            '/table': 'Manage Tables',
            '/room': 'Manage Rooms',
            '/food': 'Manage Foods',
            '/order': 'Manage Orders',
            '/guest': 'Manage Guests',
            '/reservation': 'Manage Reservations',
            '/report': 'Reports',
            '/expense': 'Expense Management',
            '/expense-category': 'Expense Categories',
            '/inventory-category': 'Inventory Categories',
            '/inventory': 'Inventory Management',
            '/profile': 'Profile'
        };
        return titleMap[path] || 'Dashboard';
    };

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleLogout = () => {
        localStorage.removeItem('billing_app_token');
        handleCloseUserMenu();
        navigate('/login');
    };

    const handleProfile = () => {
        handleCloseUserMenu();
        navigate('/profile');
    };

    return (
        <>
            <AppBar 
                position="fixed" 
                open={open} 
                drawerwidth={drawerWidth}
                sx={{
                    background: 'linear-gradient(135deg, #8b44ff 0%, #6b2fc7 100%)',
                    boxShadow: '0 4px 20px rgba(139, 68, 255, 0.3)',
                }}
            >
                <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={() => setOpen(!open)}
                        edge="start"
                        sx={[
                            { 
                                mr: 2,
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                }
                            }, 
                            open && { display: 'none' }
                        ]}
                    >
                        <MenuIcon style={{ color: "white" }} />
                    </IconButton>

                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            color: 'white',
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        {getPageTitle()}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Tooltip title="Open settings">
                            <IconButton 
                                onClick={handleOpenUserMenu} 
                                sx={{ 
                                    p: 0,
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                    }
                                }}
                            >
                                <AccountCircle sx={{ color: "white", fontSize: 32 }} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                            keepMounted
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{ 
                                mt: '10px', 
                                '& .MuiPaper-root': {
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    minWidth: 150,
                                }
                            }}
                        >
                            <MenuItem 
                                onClick={handleProfile}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(139, 68, 255, 0.08)',
                                    }
                                }}
                            >
                                <Typography textAlign="center">Profile</Typography>
                            </MenuItem>
                            <MenuItem 
                                onClick={handleLogout}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 72, 66, 0.08)',
                                    }
                                }}
                            >
                                <Typography textAlign="center" color="error">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
        </>
    );
};

export default Appbar;

